"use client";

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, GripVertical, Eye, EyeOff } from "lucide-react";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgToast } from "@/components/fg-toast";
import { FgDialog } from "@/components/fg-dialog";
import { api } from "@/lib/api";

interface Faq {
  id: string;
  question: string;
  answer: string;
  sortOrder: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export function FaqPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Faq | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: faqs = [], isLoading } = useQuery<Faq[]>({
    queryKey: ["admin", "faqs"],
    queryFn: async () => (await api.get("/api/admin/faqs")).data,
  });

  const deleteFaq = useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/api/admin/faqs/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "faqs"] });
      FgToast.success("FAQ deleted");
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Delete failed"),
  });

  const togglePublish = useMutation({
    mutationFn: async ({ id, isPublished }: { id: string; isPublished: boolean }) =>
      (await api.put(`/api/admin/faqs/${id}`, { isPublished })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "faqs"] });
    },
    onError: (e) =>
      FgToast.error((e as { message?: string }).message ?? "Update failed"),
  });

  return (
    <AdminShell>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white">FAQ</h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            Manage the questions shown on the landing page.
          </p>
        </div>
        <FgButton onClick={() => setCreating(true)}>
          <Plus size={14} /> New FAQ
        </FgButton>
      </div>

      <FgCard className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)]">
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#8E8E93] uppercase tracking-wider w-8">#</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Question</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#8E8E93] uppercase tracking-wider hidden md:table-cell">Answer preview</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-[#8E8E93] uppercase tracking-wider w-24">Published</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-[#8E8E93] uppercase tracking-wider w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[#8E8E93]">
                  Loading…
                </td>
              </tr>
            )}
            {faqs.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[#8E8E93]">
                  No FAQs yet. Click "New FAQ" to create one.
                </td>
              </tr>
            )}
            {faqs.map((faq) => (
              <tr
                key={faq.id}
                className="border-b border-[rgba(255,255,255,0.04)] hover:bg-white/[0.02] transition-colors"
              >
                <td className="px-4 py-3 text-[#8E8E93]">
                  <GripVertical size={14} className="cursor-grab opacity-40" />
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-white line-clamp-2">{faq.question}</p>
                  <p className="mt-0.5 text-xs text-[#8E8E93]">Order: {faq.sortOrder}</p>
                </td>
                <td className="px-4 py-3 text-[#8E8E93] line-clamp-2 hidden md:table-cell">
                  {faq.answer}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() =>
                      togglePublish.mutate({ id: faq.id, isPublished: !faq.isPublished })
                    }
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold transition-colors ${
                      faq.isPublished
                        ? "bg-[#34C759]/15 text-[#34C759]"
                        : "bg-[#FF453A]/15 text-[#FF453A]"
                    }`}
                  >
                    {faq.isPublished ? (
                      <>
                        <Eye size={11} /> Live
                      </>
                    ) : (
                      <>
                        <EyeOff size={11} /> Hidden
                      </>
                    )}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <FgButton
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing(faq)}
                    >
                      <Pencil size={13} />
                    </FgButton>
                    <FgButton
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Delete FAQ "${faq.question.slice(0, 40)}…"?`))
                          deleteFaq.mutate(faq.id);
                      }}
                    >
                      <Trash2 size={13} className="text-[#E63946]" />
                    </FgButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </FgCard>

      {creating && (
        <FaqEditor
          onClose={() => setCreating(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "faqs"] });
            setCreating(false);
          }}
        />
      )}

      {editing && (
        <FaqEditor
          faq={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "faqs"] });
            setEditing(null);
          }}
        />
      )}
    </AdminShell>
  );
}

function FaqEditor({
  faq,
  onClose,
  onSaved,
}: {
  faq?: Faq;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [question, setQuestion] = useState(faq?.question ?? "");
  const [answer, setAnswer] = useState(faq?.answer ?? "");
  const [sortOrder, setSortOrder] = useState(faq?.sortOrder ?? 0);
  const [isPublished, setIsPublished] = useState(faq?.isPublished ?? true);
  const [saving, setSaving] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      setSaving(true);
      if (faq) {
        return (
          await api.put(`/api/admin/faqs/${faq.id}`, {
            question,
            answer,
            sortOrder,
            isPublished,
          })
        ).data;
      } else {
        return (
          await api.post(`/api/admin/faqs`, {
            question,
            answer,
            sortOrder,
            isPublished,
          })
        ).data;
      }
    },
    onSuccess: () => {
      FgToast.success(faq ? "FAQ updated" : "FAQ created");
      onSaved();
    },
    onError: (e) => {
      setSaving(false);
      FgToast.error((e as { message?: string }).message ?? "Save failed");
    },
  });

  return (
    <FgDialog
      open={true}
      title={faq ? "Edit FAQ" : "New FAQ"}
      size="lg"
      onClose={onClose}
      confirmOnClose
      confirmMessage="Discard changes?"
      footer={
        <>
          <FgButton variant="ghost" onClick={onClose}>
            Cancel
          </FgButton>
          <FgButton
            onClick={() => save.mutate()}
            disabled={saving || !question.trim() || !answer.trim()}
          >
            {saving ? "Saving…" : "Save FAQ"}
          </FgButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">
            Question
          </label>
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={2}
            className="w-full rounded-lg bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] px-3 py-2.5 text-sm text-white placeholder:text-[#8E8E93] focus:border-[#FFD60A] focus:outline-none resize-none"
            placeholder="e.g. Do I need any German knowledge to start?"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">
            Answer
          </label>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={4}
            className="w-full rounded-lg bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] px-3 py-2.5 text-sm text-white placeholder:text-[#8E8E93] focus:border-[#FFD60A] focus:outline-none resize-none"
            placeholder="Write the full answer shown to users…"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">
              Sort order
            </label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              className="w-full rounded-lg bg-[#0A0A0A] border border-[rgba(255,255,255,0.1)] px-3 py-2.5 text-sm text-white focus:border-[#FFD60A] focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[#8E8E93]">
              Visibility
            </label>
            <div className="flex items-center gap-3 h-full pt-2">
              <button
                type="button"
                onClick={() => setIsPublished(true)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                  isPublished
                    ? "bg-[#34C759]/20 text-[#34C759]"
                    : "bg-[rgba(255,255,255,0.06)] text-[#8E8E93]"
                }`}
              >
                <Eye size={11} /> Published
              </button>
              <button
                type="button"
                onClick={() => setIsPublished(false)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                  !isPublished
                    ? "bg-[#FF453A]/20 text-[#FF453A]"
                    : "bg-[rgba(255,255,255,0.06)] text-[#8E8E93]"
                }`}
              >
                <EyeOff size={11} /> Hidden
              </button>
            </div>
          </div>
        </div>
      </div>
    </FgDialog>
  );
}
